/* eslint-disable max-lines */
import { composeRefs } from '@radix-ui/react-compose-refs';
import * as React from 'react';

import { InlineInput } from '@/components/ui/inline-input';
import Loader from '@/components/ui/loader';
import { cn } from '@/lib/cn';
import * as Cmd from '@heidi/design-system/command';
import {
  TypographyCaption,
  TypographyCaptionBold,
  typographyVariant,
} from '@heidi/design-system/typography';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  DropdownFooter,
  EhrPatientResultRow,
  LinkedPatientChip,
  PatientResultRow,
  SuggestedMatchReviewDialog,
  SuggestedMatchesSection,
  computeCreateMatchMethod,
  computeLinkMatchMethod,
  toSuggestedMatchSearchResult,
} from '@heidi/patients';
import { toEhrPatientSearchResultView } from '../../adapters/to-ehr-patient-search-result-view';
import { EHR_PATIENT_SEARCH_MIN_QUERY_LENGTH } from '../../constants';
import { LinkedPatientHoverCard } from './linked-patient-hover-card';
import type { PatientSearchComboboxProps } from './patient-search-combobox.types';

const CREATE_VALUE = '__create__';

function isImeConfirmKey(event: React.KeyboardEvent): boolean {
  return event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229;
}

const RENAME_VALUE = '__rename__';
const SEARCH_EHR_VALUE = '__search_ehr__';
const FULL_ROSTER_SEARCH_VALUE = '__full_roster_search__';
export const PatientSearchCombobox = React.forwardRef<
  HTMLDivElement,
  PatientSearchComboboxProps
>(
  (
    {
      variant = 'inline',
      query,
      onQueryChange,
      onCompositionStart,
      onCompositionEnd,
      results,
      suggestedMatches = [],
      autoLinkCandidate,
      committedName = '',
      isFetching = false,
      beIsFresh = true,
      isError = false,
      disabled = false,
      placeholder,
      linkedPatientName,
      onLinkedNameClick,
      linkedPatientPopover,
      onFieldFocus,
      onSelectExisting,
      onCreate,
      onSubmitFreeText,
      ehrPatientSearch,
      inputRef: inputRefProp,
      'data-testid': testId,
      inputTestId,
    },
    ref
  ) => {
    const intl = useIntl();
    const defaultInputRef = React.useRef<HTMLInputElement>(null);
    const inputRef = inputRefProp ?? defaultInputRef;
    const wrapperRef = React.useRef<HTMLDivElement>(null);
    const [hasFocus, setHasFocus] = React.useState(false);
    const [isReviewingSuggestedMatches, setIsReviewingSuggestedMatches] =
      React.useState(false);
    // Tracks whether the most recent selection-relevant input was a mouse
    // click or a keyboard press. handleSelect reads it to attribute
    // `match_method` (`explicit_click` vs `highlighted_default` /
    // `typed_then_picked`) for analytics. Set on dropdown-area mousedown
    // (inline floating list, or the popover Command that contains it) and
    // on input Enter keydown; cleared on focus so each interaction starts
    // neutral.
    const lastInputMethodRef = React.useRef<'click' | 'keyboard' | null>(null);

    // 'popover' mode is rendered inside a Radix Popover/Drawer that owns its
    // own open/dismiss lifecycle. The combobox's inline-only machinery
    // (floating dropdown, focus-gated visibility, document click-outside,
    // linked-chip branch, committedName blur display) is all skipped.
    const isPopover = variant === 'popover';

    // Document-level mousedown is more robust than the wrapper's onBlur
    // for click-outside dismissal — onBlur can stay stuck when focus is
    // returned after a drawer/dialog close or when the activeElement is
    // restored to the input after a re-render. Skipped in popover mode —
    // the Popover/Drawer handles outside-click dismissal itself.
    React.useEffect(() => {
      if (!hasFocus || isPopover) return;
      const handle = (event: MouseEvent) => {
        if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
          setIsReviewingSuggestedMatches(false);
          setHasFocus(false);
        }
      };
      document.addEventListener('mousedown', handle);
      return () => document.removeEventListener('mousedown', handle);
    }, [hasFocus, isPopover]);

    const trimmedQuery = query.trim();
    const hasActiveSearch = trimmedQuery.length > 0;
    const hasSuggestedMatches = suggestedMatches.length > 0;
    const firstResultId = hasActiveSearch ? results[0]?.id : undefined;

    // Controlled cmdk highlight — Enter activates the highlighted row.
    // Priority: auto-link candidate → first result → Create CTA. The
    // Create fallback means Enter always has *some* visible target,
    // even when search returns nothing.
    const [cmdkValue, setCmdkValue] = React.useState<string>(
      autoLinkCandidate?.id ?? firstResultId ?? CREATE_VALUE
    );

    React.useEffect(() => {
      setCmdkValue(autoLinkCandidate?.id ?? firstResultId ?? CREATE_VALUE);
    }, [query, autoLinkCandidate?.id, firstResultId]);

    const showDropdown = hasFocus && !isReviewingSuggestedMatches;
    const showSuggestedMatches = hasSuggestedMatches && !hasActiveSearch;
    // External EHR roster section (when the controller passes one). Search is
    // non-live; the three states are mutually exclusive for the typed query:
    //   1. Ready to search  → only "Search {ehr} for …"
    //   2. Hits for query   → result rows
    //   3. Empty for query  → soft-error copy + "Search all {ehr} patients"
    // Stale empty/results from a previous submit are hidden as soon as the
    // typed query diverges from `submittedQuery`.
    const ehrResults = ehrPatientSearch?.results ?? [];
    const ehrSubmittedQuery = ehrPatientSearch?.submittedQuery ?? '';
    const ehrQueryMatchesSubmit =
      Boolean(ehrSubmittedQuery) && trimmedQuery === ehrSubmittedQuery;
    const canSubmitEhrSearch =
      Boolean(ehrPatientSearch) &&
      hasActiveSearch &&
      trimmedQuery.length >= EHR_PATIENT_SEARCH_MIN_QUERY_LENGTH &&
      !ehrQueryMatchesSubmit;
    const showEhrResults =
      ehrQueryMatchesSubmit && !ehrPatientSearch?.isFetching && ehrResults.length > 0;
    const showEhrEmpty =
      ehrQueryMatchesSubmit && !ehrPatientSearch?.isFetching && ehrResults.length === 0;
    // Only mount the EHR section heading when there is actionable content —
    // avoids an empty "Link from {ehr}" group on focus with a short/empty query.
    const showEhrSection =
      Boolean(ehrPatientSearch) &&
      (canSubmitEhrSearch ||
        showEhrResults ||
        showEhrEmpty ||
        Boolean(ehrPatientSearch?.isFetching));
    const showEmptyState =
      !showSuggestedMatches &&
      results.length === 0 &&
      !isFetching &&
      !isError &&
      beIsFresh &&
      !showEhrResults &&
      !showEhrEmpty &&
      !ehrPatientSearch?.isFetching;
    const fallbackPlaceholder = intl.formatMessage({
      defaultMessage: 'Add patient identifier',
      id: '2q3Lmh9Z1r',
    });
    // Single dispatcher for every selectable row (results + footer
    // actions). cmdk fires this when the user clicks a row OR presses
    // Enter on the highlighted row — we don't intercept either. We do
    // attribute match_method here from `lastInputMethodRef` plus whether
    // the chosen row is the auto-link candidate.
    const handleSelect = (value: string) => {
      // Read-only / permission-blocked sessions must not link or create.
      if (disabled) return;
      if (value === CREATE_VALUE) {
        onCreate(trimmedQuery, {
          matchMethod: computeCreateMatchMethod(trimmedQuery.length, results.length),
          resultCountSeen: results.length,
        });
        return;
      }
      if (value === RENAME_VALUE) {
        if (trimmedQuery.length > 0) {
          onSubmitFreeText(trimmedQuery);
          // cmdk's onSelect can leave focus on the CommandItem (a
          // descendant of the wrapper), so click-outside doesn't fire.
          // Force-dismiss here.
          setHasFocus(false);
          inputRef.current?.blur();
        }
        return;
      }
      if (value === SEARCH_EHR_VALUE) {
        ehrPatientSearch?.onSubmitSearch(trimmedQuery);
        return;
      }
      if (value === FULL_ROSTER_SEARCH_VALUE) {
        ehrPatientSearch?.onOpenFullRosterSearch(trimmedQuery);
        setHasFocus(false);
        inputRef.current?.blur();
        return;
      }
      const matchIndex = results.findIndex((r) => r.id === value);
      const match = matchIndex >= 0 ? results[matchIndex] : undefined;
      if (!match) return;
      onSelectExisting(match, {
        matchMethod: computeLinkMatchMethod(
          lastInputMethodRef.current === 'click',
          match.id === autoLinkCandidate?.id
        ),
        resultPosition: matchIndex,
        resultCountSeen: results.length,
      });
    };

    // IME confirm (Enter / keyCode 229) must not be treated as a
    // Create/select key. cmdk skips composing events on Command, but
    // the empty-results Create path lives on this input handler and
    // fires first — without this guard, confirming a CJK candidate
    // creates a patient from the partial string.
    // Arrow keys + Enter on results are dispatched by cmdk (results
    // live inside CommandList). DropdownFooter intentionally sits outside
    // CommandList so arrow navigation stays in results; when there are no
    // results we manually fire Create on Enter so keyboard users can
    // dispatch the only available action without having to mouse-click.
    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && !isImeConfirmKey(event)) {
        // Record input method BEFORE cmdk's own keyboard handling
        // dispatches onSelect, so handleSelect can attribute
        // match_method correctly. Also covers the manual Enter-on-empty
        // path below.
        lastInputMethodRef.current = 'keyboard';
      }
      if (event.key === 'Escape') {
        // Popover mode: let Escape propagate so the surrounding Radix
        // Popover/Drawer dismisses (its own behaviour). Inline mode has no
        // surrounding layer, so it clears the field and blurs instead.
        if (isPopover) return;
        onQueryChange('');
        setHasFocus(false);
        inputRef.current?.blur();
        return;
      }
      if (
        event.key === 'Enter' &&
        results.length === 0 &&
        beIsFresh &&
        !isImeConfirmKey(event)
      ) {
        event.preventDefault();
        handleSelect(CREATE_VALUE);
      }
    };

    const handleDropdownMouseDown = () => {
      lastInputMethodRef.current = 'click';
    };

    const handleConfirmSuggestedMatch = (matchId: string) => {
      if (disabled) return;
      const matchIndex = suggestedMatches.findIndex((match) => match.id === matchId);
      const match = matchIndex >= 0 ? suggestedMatches[matchIndex] : undefined;
      if (!match) return;

      setIsReviewingSuggestedMatches(false);
      onSelectExisting(toSuggestedMatchSearchResult(match), {
        matchMethod: 'explicit_click',
        resultPosition: matchIndex,
        resultCountSeen: suggestedMatches.length,
      });
    };

    const handleFocus = () => {
      // Don't open the dropdown for a read-only / permission-blocked session.
      if (disabled) return;
      setHasFocus(true);
      lastInputMethodRef.current = null;
      onFieldFocus?.();
    };

    // Result list + footer. Identical in both variants — only the input
    // chrome and the container around this differ. In 'inline' mode it sits
    // inside the focus-gated floating dropdown; in 'popover' mode it renders
    // directly inside the Popover/Drawer body. Sharing one body keeps the
    // suggested-match section, results, and Create / Create-unlinked-session
    // footer from drifting between the in-session header and the session list.
    const selectionBody = (
      <>
        {/* Create sits at the top only while the suggested list is
            showing (session merge on + not searching). Once the user
            types — or when there are no suggested matches (e.g. session
            merge off) — it drops to the bottom so filtered results render
            above it, the pre-existing new-linking-UI layout. */}
        {showSuggestedMatches && (
          <DropdownFooter
            trimmedQuery={trimmedQuery}
            createValue={CREATE_VALUE}
            renameValue={RENAME_VALUE}
            onSelect={handleSelect}
            className="border-b"
          />
        )}
        <Cmd.CommandList id={isPopover ? undefined : 'patient-search-list'}>
          {showSuggestedMatches && (
            <>
              <TypographyCaptionBold className="px-2 pb-1 pt-2">
                <FormattedMessage
                  defaultMessage="Suggested"
                  id="xzEtjB1ENf"
                  description="Heading for suggested patient matches in the patient selector"
                />
              </TypographyCaptionBold>
              <SuggestedMatchesSection
                matches={suggestedMatches}
                onSelectMatch={(match) => handleConfirmSuggestedMatch(match.id)}
              />
            </>
          )}
          {isError && (
            <div
              role="status"
              aria-live="polite"
              className="border-t border-border py-3 text-center"
              data-testid="patient-search-error"
            >
              <TypographyCaption className="text-text-secondary">
                <FormattedMessage
                  defaultMessage="Search unavailable, try again"
                  id="XSkhKhnv1v"
                  description="Notice shown in the patient picker when the patient search request failed"
                />
              </TypographyCaption>
            </div>
          )}
          {showEmptyState && (
            <Cmd.CommandEmpty className="py-3" aria-live="polite">
              <TypographyCaption className="text-text-secondary">
                <FormattedMessage
                  defaultMessage="No matching patients"
                  id="N1YEOgpkSv"
                  description="Empty state when patient search has no matching results"
                />
              </TypographyCaption>
            </Cmd.CommandEmpty>
          )}
          {results.length > 0 && (
            <Cmd.CommandGroup className={isPopover ? 'p-0' : undefined}>
              {results.map((result) => (
                <PatientResultRow
                  key={result.id}
                  result={result}
                  onSelect={handleSelect}
                />
              ))}
            </Cmd.CommandGroup>
          )}
          {showEhrSection && ehrPatientSearch && (
            <>
              <div
                className={cn(
                  'flex items-center gap-1.5 px-3 pb-1 pt-2',
                  (results.length > 0 || showSuggestedMatches) &&
                    'mt-1 border-t border-border'
                )}
              >
                {ehrPatientSearch.icon}
                <TypographyCaptionBold className="text-text-secondary">
                  <FormattedMessage
                    defaultMessage="Link from {ehrName}"
                    id="kz1dP0wIN1"
                    description="Heading for the external EHR patient results section in the patient selector"
                    values={{ ehrName: ehrPatientSearch.ehrName }}
                  />
                </TypographyCaptionBold>
                {ehrPatientSearch.isFetching && <Loader size={12} />}
              </div>
              <Cmd.CommandGroup className={isPopover ? 'p-0' : undefined}>
                {canSubmitEhrSearch && (
                  <Cmd.CommandItem
                    value={SEARCH_EHR_VALUE}
                    onSelect={() => handleSelect(SEARCH_EHR_VALUE)}
                    className="px-3 py-2.5 text-text-primary data-[selected=true]:bg-surface-pressed"
                    data-testid="ehr-submit-search"
                  >
                    <TypographyCaption className="text-text-primary">
                      <FormattedMessage
                        defaultMessage="Search {ehrName} for “{query}”"
                        id="zAOkNf7yUa"
                        description="Explicit (non-live) external EHR patient search action in the find-or-link dropdown"
                        values={{
                          ehrName: ehrPatientSearch.ehrName,
                          query: trimmedQuery,
                        }}
                      />
                    </TypographyCaption>
                  </Cmd.CommandItem>
                )}
                {showEhrResults &&
                  ehrResults.map((result) => (
                    <EhrPatientResultRow
                      key={result.patient_id}
                      result={toEhrPatientSearchResultView(result)}
                      // Closes over the WIRE row rather than reading the row
                      // back off the callback: the section's handler is typed on
                      // `IntegrationPatientSearchResultItem`, and the row hands
                      // back the view it rendered.
                      onSelect={() => ehrPatientSearch.onSelect(result)}
                    />
                  ))}
                {showEhrEmpty && (
                  <>
                    <div
                      className="px-3 py-2"
                      data-testid="ehr-search-empty"
                      role="status"
                    >
                      <TypographyCaption className="text-validation-warning">
                        <FormattedMessage
                          defaultMessage="No recent {ehrName} matches for this name. Try searching the full patient list."
                          id="axfzT+Iqnk"
                          description="Soft-error empty state after combobox EHR search returns no hits; prompts full-roster search"
                          values={{ ehrName: ehrPatientSearch.ehrName }}
                        />
                      </TypographyCaption>
                    </div>
                    <Cmd.CommandItem
                      value={FULL_ROSTER_SEARCH_VALUE}
                      onSelect={() => handleSelect(FULL_ROSTER_SEARCH_VALUE)}
                      className="px-3 py-2.5 text-text-primary data-[selected=true]:bg-surface-pressed"
                      data-testid="ehr-full-roster-search"
                    >
                      <TypographyCaption className="text-text-primary">
                        <FormattedMessage
                          defaultMessage="Search all {ehrName} patients"
                          id="ytjLWp9gHd"
                          description="CTA to open full EHR roster search via the provider link-patient flow"
                          values={{ ehrName: ehrPatientSearch.ehrName }}
                        />
                      </TypographyCaption>
                    </Cmd.CommandItem>
                  </>
                )}
              </Cmd.CommandGroup>
            </>
          )}
        </Cmd.CommandList>
        {!showSuggestedMatches && (
          <DropdownFooter
            trimmedQuery={trimmedQuery}
            createValue={CREATE_VALUE}
            renameValue={RENAME_VALUE}
            onSelect={handleSelect}
          />
        )}
      </>
    );

    // 'popover' mode: the Popover/Drawer is the container, so the body is
    // always visible (no focus gate, no floating wrapper) and the input is a
    // fixed-width CommandInput. Linked-chip chrome stays on `'inline'`
    // (session header); this mode is input + results only.
    if (isPopover) {
      return (
        <Cmd.Command
          ref={ref}
          data-testid={testId}
          className="gap-0.5 rounded-lg p-1"
          shouldFilter={false}
          label={placeholder ?? fallbackPlaceholder}
          value={cmdkValue}
          onValueChange={setCmdkValue}
          onMouseDown={handleDropdownMouseDown}
        >
          <Cmd.CommandInput
            placeholder={placeholder ?? fallbackPlaceholder}
            className="h-9 py-0"
            // Drop the input's built-in bottom border so it doesn't stack with
            // the DropdownFooter's top border (which is the single separator,
            // matching the in-session header dropdown).
            wrapperClassName="border-b-0"
            value={query}
            onValueChange={onQueryChange}
            showClearButton
            disabled={disabled}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onCompositionStart={onCompositionStart}
            onCompositionEnd={onCompositionEnd}
            data-testid={inputTestId}
          />
          {selectionBody}
        </Cmd.Command>
      );
    }

    // Linked-state branch skips the dropdown after all hook calls.
    if (linkedPatientName) {
      // The hover card is composed here rather than inside the chip: it reads
      // the current session id and emits `patient_recap_popover_opened`, both
      // app-side, and this is the surface that already owns
      // `linkedPatientPopover`. Same conditional the chip used to apply
      // internally — omit the popover and the bare chip renders.
      const chip = (
        <LinkedPatientChip
          ref={ref}
          name={linkedPatientName}
          onClick={onLinkedNameClick}
          data-testid={testId}
        />
      );
      if (!linkedPatientPopover) return chip;
      return (
        <LinkedPatientHoverCard popover={linkedPatientPopover}>
          {chip}
        </LinkedPatientHoverCard>
      );
    }

    return (
      <div
        ref={composeRefs(ref, wrapperRef)}
        data-testid={testId}
        className="relative inline-block"
      >
        <Cmd.Command
          className="!size-auto overflow-visible bg-transparent"
          shouldFilter={false}
          value={cmdkValue}
          onValueChange={setCmdkValue}
        >
          {/* InlineInput auto-sizes the title field; cmdk handles keys at the Command root. */}
          <InlineInput
            inputRef={inputRef}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={showDropdown}
            aria-controls="patient-search-list"
            // Focused: show the typed query (live edit).
            // Blurred: show the committed name (last persisted value)
            // regardless of whether the user has typed something new.
            // The unsaved typed text is preserved in state and reappears
            // on refocus — so blur looks like "no edit happened" rather
            // than "your name vanished". Empty committedName falls
            // through to the placeholder.
            value={hasFocus ? query : committedName}
            onChange={(e) => onQueryChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onCompositionStart={onCompositionStart}
            onCompositionEnd={onCompositionEnd}
            onFocus={handleFocus}
            placeholder={placeholder ?? fallbackPlaceholder}
            disabled={disabled}
            wrapperClassName="border-none px-0"
            className={cn(
              typographyVariant({ variant: 'h4' }),
              '!h-auto whitespace-normal placeholder:text-text-secondary'
            )}
            maxWidth="600px"
          />

          {showDropdown && (
            <div
              onMouseDown={handleDropdownMouseDown}
              className="absolute start-0 top-full z-20 mt-1 w-max min-w-[280px] max-w-[80vw] overflow-hidden rounded-md border border-border bg-surface-elevated shadow-md"
            >
              {selectionBody}
            </div>
          )}
        </Cmd.Command>
        <SuggestedMatchReviewDialog
          open={isReviewingSuggestedMatches}
          onOpenChange={setIsReviewingSuggestedMatches}
          matches={suggestedMatches}
          onConfirmMatch={(match) => handleConfirmSuggestedMatch(match.id)}
          onDismiss={() => setIsReviewingSuggestedMatches(false)}
        />
      </div>
    );
  }
);
PatientSearchCombobox.displayName = 'PatientSearchCombobox';
